import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { orderLineService } from "/app/src/services";

import { Search } from "/app/src/components/generic/components/search";

import { useTranslation } from "react-i18next";
import { OrderLine as OrderLineType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Renders the OrderLines component, which displays a paginated and sortable table of order lines.
 *
 * @returns {JSX.Element} The rendered OrderLines component.
 */
export default function OrderLines() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );
  const columnHelper = createColumnHelper<OrderLineType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("number", {
        id: "number",
        cell: (info) => info.getValue(),
        header: () => t("translation:number"),
      }),
      columnHelper.accessor("Info1", {
        id: "Info1",
        cell: (info) => info.getValue(),
        header: () => t("translation:info1"),
      }),
    ],
    [columnHelper, t],
  );

  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );

  const goToOrderLine = useCallback(
    (orderLineId: number) => {
      navigate(`/explorer/order_lines/${orderLineId}`);
    },
    [navigate],
  );

  const {
    data: orderLines,
    count: orderLinesCount,
    isFetching,
  } = usePaginatedData<OrderLineType>({
    queryKey: ["order_lines"],
    searchString,
    sort,
    page,
    pageSize,
    service: orderLineService,
    options: {
      state: "queued",
    },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: orderLinesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:order_lines")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={orderLines}
        tableColumns={columns}
        length={orderLinesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToOrderLine}
        emptyText={t("translation:no_order_lines")}
      />
    </div>
  );
}
